import React, { ReactNode, useEffect } from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useHreflang } from "../../i18n/hooks/useHreflang";
import { getLocaleFromPath, localePath } from "../../i18n/config";
import { metadataTranslations } from "../../i18n/metadata";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useLocation } from "react-router";
import Button from "../../components/button/Button";
import Banner from "../../components/layout/banner/Banner";
import { getAlertsRequest } from "../../redux/actions";
import { AppState } from "../../redux/types";

export type ServicesType = {
    content: ReactNode;
    image: string;
    credit?: string;
    alt?: string;
    contain?: boolean;
};
const VideoCallApp = () => {
    const hreflang = useHreflang("videopuhelu_appi");
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const locale = getLocaleFromPath(pathname);
    const meta = metadataTranslations.videoCallApp[locale] || metadataTranslations.videoCallApp.fi;

    const currentDate = Date.now();

    useEffect(() => {
        dispatch(getAlertsRequest());
        return () => {};
    }, [dispatch]);

    const resources = useSelector((state: AppState) => state.alerts);
    const { list } = resources;

    const appOnlyImage = locale === "en" ? "/assets/images/videocallApp/en-app-only.png" : locale === "sv" ? "/assets/images/videocallApp/swe-app-only.png" : "/assets/images/videocallApp/app_only_tablet.png";
    const service2Image = locale === "en" ? "/assets/images/videocallApp/service-2-en-apponly.png" : locale === "sv" ? "/assets/images/videocallApp/service-2-sv-apponly.png" : "/assets/images/videocallApp/videoapp_3.jpg";
    const videoCallYoutubeUrl = locale === "sv" ? "https://youtu.be/ujfjfUBvaNU" : locale === "en" ? "https://youtu.be/0rKnAb-bMoA" : "https://youtu.be/Jn4GRQo5vMc";
    const videoCallYoutubeId = locale === "sv" ? "ujfjfUBvaNU" : locale === "en" ? "0rKnAb-bMoA" : "Jn4GRQo5vMc";

    const services: ServicesType[] = [
        {
            content: (
                <>
                    <div className="services-info-title">Helppo Seniorivideopuhelu appi</div>
                    <ul className="services-info-text">
                        <li>Seniorille ja 6 läheiselle</li>
                        <li>Helppo vastata – videopuhelu voi avautua ilman kosketusta</li>
                        <li>Helppo soittaa – painamalla läheisen isoa kuvaa</li>
                        <li>Läheisille Android- ja Apple- laitteisiin, myös ulkomailla</li>
                        <li>
                            Seniorille suosittelemme Android- laitteisiin, videopuhelu voi avautua automaattisesti vain <strong>Android</strong>-laitteessa
                        </li>
                        <li>Ryhmäpuhelut</li>
                        <li>Turvallinen, vain perhe voi soittaa!</li>
                    </ul>
                </>
            ),
            image: "/assets/images/videocallApp/app_only_tablet_user.png",
            alt: "Nainen pitää kädessään sinistä puhelinta",
        },

        {
            content: (
                <>
                    <div className="services-info-title">Helposti käyttöön!</div>

                    <ul className="services-info-text">
                        <li>
                            Yksi läheinen tilaa palvelun ja lisää muut perhetiliin <strong>Kokeile maksutta!</strong>
                        </li>
                        <li>Käyttäjä lataa apin omalle laitteelle</li>
                        <li>Käyttäjä kirjautuu appiin, lataa kuvan, jos haluaa, ja kaikki toimii!</li>
                        <li>
                            Läheinen voi hoitaa kaiken seniorin puolesta!{" "}
                            <a href={videoCallYoutubeUrl} className="lue-lisää-linkki" target="_blank" rel="noreferrer">
                                <strong>Katso video</strong>
                            </a>{" "}
                        </li>
                    </ul>
                </>
            ),
            image: service2Image,
            alt: "Älypuhelin pöydällä näyttää videopuhelua, jossa on nainen",
        },
        {
            content: (
                <>
                    <div className="services-info-title">Kokeile ilmaiseksi!</div>
                    <ul className="services-info-text">
                        <li>
                            <u>Kokeile maksutta</u> 14 päivää!
                        </li>
                        <li>Tämän jälkeen 9,50e/kk koko perheeltä!</li>
                        <li>Helpot ohjeet kaikille sähköpostiin.</li>
                    </ul>
                </>
            ),
            image: appOnlyImage,
            alt: "Uusi Samsung Galaxy S10 5G ja kosketusnäyttökynä pöydällä. ",
        },
        {
            content: (
                <>
                    <div className="services-info-title">Mitä tarvitsette?</div>
                    <ul className="services-info-text">
                        <li>
                            Apple tai Android laitteet
                            <ul>
                                <li>Seniorille suositus Android, jossa Digihappyvideopuhelu voi avautua automaattisesti</li>
                                <li>Ios 13 ja Android 11 tai uudempi</li>
                            </ul>
                        </li>

                        <li>Dataliittymät tai wifi</li>
                        <li>
                            Tilatessa käyttäjien sähköpostit
                            <ul>
                                <li>seniori ei tarvitse sähköpostia</li>
                                <li>käyttäjiä voi lisätä myöhemmin</li>
                            </ul>
                        </li>
                    </ul>
                </>
            ),
            image: "/assets/images/videocallApp/videoapp_4.jpg",
        },
    ];

    // const [play, setPlay] = useState(0);

    return (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <link rel="alternate" hrefLang="fi" href={hreflang.fi} />
                <link rel="alternate" hrefLang="en" href={hreflang.en} />
                <link rel="alternate" hrefLang="sv" href={hreflang.sv} />
                <link rel="alternate" hrefLang="x-default" href={hreflang.fi} />
            </Helmet>
            <Banner
                backgroundImage="/assets/images/videocallApp/videocall_senior.jpg"
                label="Nainen pitelee älypuhelinta, jonka näytöllä näkyy yhteystietoluettelo"
                height={`calc(100vh - ${
                    56 +
                    56 *
                        list?.filter((el) => {
                            // @ts-ignore
                            return (!el.start || new Date(el.start) <= currentDate) && (!el.end || new Date(el.end) >= currentDate);
                        })?.length -
                    1
                }px)`}
                className="banner-zoom-out"
            >
                <div className="global-banner-text-container" style={{ maxWidth: "100%", width: "100%" }}>
                    <h1 className="global-banner-title" style={{ alignSelf: "end" }}>
                        <img src={"/assets/images/videocallApp/logo.png"} alt="emoji" height={40} width={40} /> Seniori Videopuhelu appi
                    </h1>
                    <p className="global-banner-text" style={{ alignSelf: "end" }}>
                        Seniorille puhelu voi avautua ilman kosketusta
                    </p>
                    <p className="global-banner-text video-call-mobile-left" style={{ alignSelf: "end" }}>
                        Yhdistä perheesi!
                    </p>
                    <Link to={localePath("osta", "videopuhelu_appi")} style={{ marginTop: "auto" }}>
                        <Button secondary emphasized>
                            Kokeile maksutta{" "}
                        </Button>
                    </Link>
                </div>
            </Banner>
            <Container fluid className="my-3 services-container services-container-80" style={{ minWidth: "min(900px, 100%)" }}>
                <h1 className="services-header">
                    <strong>Automaattisesti avautuvat videopuhelut </strong> omille laitteille. <strong> Kokeile maksutta!</strong>
                </h1>
                <div className="global-box-line-container">
                    {services.map((service, key) => (
                        <div className="global-box-line" key={key}>
                            {key % 2 === 0 ? (
                                <React.Fragment>
                                    <div className="global-box global-background-gray">{service.content}</div>
                                    <div className="global-image-container">
                                        <img src={service.image} alt={service.alt} className="global-image" />
                                    </div>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <div className="global-image-container">
                                        <img src={service.image} alt={service.alt} className="global-image" />
                                    </div>
                                    <div className="global-box global-background-gray">{service.content}</div>
                                </React.Fragment>
                            )}
                        </div>
                    ))}
                </div>
            </Container>
            <Container fluid className="my-3 services-container service-table-container" id="palvelut">
                <div className="services-button-container">
                    <Button onClick={() => {}}>Katso video</Button>
                    <div className="global-video-wrapper" style={{ background: "#ccc" }}>
                        <iframe
                            src={`https://www.youtube.com/embed/${videoCallYoutubeId}`}
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; autoplay"
                            allowFullScreen
                            title="Digihappy Videopuhelu appi"
                            style={{
                                margin: "0 auto",
                                width: "100%",
                                height: "100%",
                                border: "none",
                                aspectRatio: 16 / 9,
                            }}
                        />
                    </div>
                </div>
            </Container>
        </>
    );
};

export default VideoCallApp;
